import { useTranslation } from 'react-i18next';

import { useGetGeneralOrganizationContacts } from '@/api/generated/general-organizations/general-organizations';
import { EmptyState, Panel, Table } from '@/shared/ui';

export interface OrganizationContactsPanelProps {
  /** Stabilní identita právě vybrané organizace; `null` = žádná není vybraná. */
  partyId: number | null;
}

/**
 * Read-only panel kontaktů organizace. Stejně jako panel adres se plní podle **právě vybrané**
 * organizace, tedy i před uložením záznamu konzumenta.
 *
 * Sloupce jsou tři, ne čtyři jako v prototypu: legacy nemá telefon jako samostatné pole — kontakt je
 * jedna hodnota a její význam určuje typ.
 */
export function OrganizationContactsPanel({ partyId }: OrganizationContactsPanelProps) {
  const { t } = useTranslation('general');

  const contactsQuery = useGetGeneralOrganizationContacts(partyId ?? 0, {
    query: { enabled: partyId != null },
  });
  const items = contactsQuery.data?.items ?? [];

  return (
    <Panel
      variant="filled"
      icon="mail"
      title={t('contacts.title')}
      subtitle={partyId == null ? undefined : t('addressBook.readOnly')}
      footer={partyId == null ? undefined : t('addressBook.managedOnOrganization')}
    >
      {partyId == null ? (
        <EmptyState
          icon="mail"
          title={t('contacts.noOrganization')}
          description={t('addressBook.noOrganizationHint')}
        />
      ) : contactsQuery.isLoading ? (
        <p className="text-phx-text-muted text-phx-sm">{t('addressBook.loading')}</p>
      ) : contactsQuery.isError ? (
        // Sem spadne i 403 uživatele bez oprávnění `general.organizations`.
        <EmptyState
          icon="alert"
          title={t('addressBook.loadError')}
          description={t('addressBook.loadErrorHint')}
        />
      ) : items.length === 0 ? (
        <EmptyState icon="mail" title={t('contacts.empty')} />
      ) : (
        <div className="border-phx-border rounded-phx overflow-x-auto border">
          <Table density="dense">
            <Table.Head>
              <Table.Row>
                <Table.HCell density="dense">{t('contacts.cols.type')}</Table.HCell>
                <Table.HCell density="dense">{t('contacts.cols.contact')}</Table.HCell>
                <Table.HCell density="dense">{t('contacts.cols.note')}</Table.HCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {items.map((item) => (
                <Table.Row key={item.contactId}>
                  <Table.Cell density="dense">{item.typeName ?? '—'}</Table.Cell>
                  <Table.Cell density="dense">{item.value ?? '—'}</Table.Cell>
                  <Table.Cell density="dense">{item.note ?? '—'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </Panel>
  );
}
